/**
 * Team service, REST over `/planning/:project_id/team*`.
 *
 * The API is the source of truth. Only ACTIVE people are mirrored into the
 * shared in-memory cache (teamMembersCache, read by assignee pickers); the
 * archived and trash lists are fetched on demand and never cached. The roster
 * carries staff e-mails and phones, so the API answers admins and owners only:
 * a lower role gets a quiet empty result from the background refresh instead
 * of an error every few seconds.
 */
import { apiDelete, apiGet, apiPost, apiPut, planningPath } from './PlanningApiClient';
import { setTeamMembersCache } from './teamMembersCache';
import { isApiError } from '../utils/apiErrors';
import { getActiveProjectId } from '../utils/session';
import type { LoginUser, TeamLifecycle, TeamMember, TeamMemberInput } from '../types/Team';

export const TEAM_CHANGE_EVENT = 'ans_team_change';

let snapshot: TeamMember[] = [];
let snapshotProject: string | null = null;

/** The active roster of the CURRENT project as last read (no network). Empty until loaded, and after a project switch. */
export function getTeamSnapshot(): TeamMember[] {
  return snapshotProject === getActiveProjectId() ? snapshot : [];
}

/** Forgets every cached person (the snapshot and the shared assignee cache). */
export function resetTeamCache(): void {
  snapshot = [];
  snapshotProject = null;
  setTeamMembersCache([]);
}

const teamPath = (suffix = '') => planningPath(`/team${suffix}`);

export async function fetchTeam(lifecycle: TeamLifecycle = 'active'): Promise<TeamMember[]> {
  const res = await apiGet<{ members?: TeamMember[] }>(teamPath(`?status=${lifecycle}`));
  return res?.members ?? [];
}

export async function fetchLoginUsers(): Promise<LoginUser[]> {
  const res = await apiGet<LoginUser[] | null>(teamPath('/users'));
  return Array.isArray(res) ? res : [];
}

/**
 * Re-reads the active roster into the shared cache. Used by the page and the
 * background sync.
 *
 * - The answer is dropped when the active project changed while it was in flight,
 *   so a late reply for project A can never fill project B's cache.
 * - A 403 (a role below admin, or a role just revoked) clears the cache, so people
 *   from an earlier project or role are not kept. With `quiet` (the background sync)
 *   it then resolves with no people; otherwise it rejects so the page can say so.
 */
export async function refreshTeamFromApi(opts: { quiet?: boolean } = {}): Promise<TeamMember[]> {
  const project = getActiveProjectId();
  if (snapshotProject !== null && snapshotProject !== project) resetTeamCache();
  try {
    const members = await fetchTeam('active');
    if (getActiveProjectId() !== project) return [];
    snapshot = members;
    snapshotProject = project;
    setTeamMembersCache(members.map(m => ({
      id: m.id, name: m.name, email: m.email ?? undefined, isActive: m.is_active, teams: m.teams ?? [],
    })));
    window.dispatchEvent(new CustomEvent(TEAM_CHANGE_EVENT, { detail: project }));
    return members;
  } catch (err) {
    if (getActiveProjectId() !== project) return [];
    if (isApiError(err) && err.status === 403) {
      resetTeamCache();
      if (opts.quiet) return [];
    }
    throw err;
  }
}

export function createTeamMember(input: TeamMemberInput): Promise<TeamMember> {
  return apiPost<TeamMember>(teamPath(), toBody(input));
}

export function updateTeamMember(id: string, input: Partial<TeamMemberInput>): Promise<TeamMember> {
  return apiPut<TeamMember>(teamPath(`/${id}`), toBody(input));
}

export const archiveTeamMember = (id: string) => apiPost<TeamMember>(teamPath(`/${id}/archive`), {});
export const trashTeamMember = (id: string) => apiPost<TeamMember>(teamPath(`/${id}/trash`), {});
export const restoreTeamMember = (id: string) => apiPost<TeamMember>(teamPath(`/${id}/restore`), {});
export const deleteTeamMemberForever = (id: string) => apiDelete<{ message: string }>(teamPath(`/${id}/permanent`));

export async function getTeamMemberById(id: string): Promise<TeamMember | null> {
  // The API lists by state; a person is in exactly one of the three.
  for (const lifecycle of ['active', 'archived', 'trash'] as const) {
    const found = (await fetchTeam(lifecycle)).find(m => m.id === id);
    if (found) return found;
  }
  return null;
}

/** The form's text becomes the JSON the API expects; empty strings clear a field. */
function toBody(input: Partial<TeamMemberInput>): Record<string, unknown> {
  const body: Record<string, unknown> = { ...input };
  for (const key of ['email', 'phone', 'whatsapp', 'role', 'notes'] as const) {
    if (typeof body[key] === 'string' && (body[key] as string).trim() === '') body[key] = null;
  }
  if (typeof body.responsibilities === 'string') {
    body.responsibilities = (body.responsibilities as string).split('\n').map(s => s.trim()).filter(Boolean);
  }
  return body;
}
